import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Turnstile, type TurnstileHandle } from "../components/Turnstile";
import { Alert } from "../components/ui/Alert";
import { Card } from "../components/ui/Card";
import { useAuth } from "../contexts/useAuth";
import {
  CONTACT_LIMITS,
  CONTACT_REQUEST_TYPES,
  submitContactForm,
  type ContactPayload,
  type ContactRequestType,
} from "../services/contactService";
import { detectBrowser, detectOS, getTimezone } from "../utils/bugReport";
import { validateEmail } from "../utils/validation";

interface ContactLocationState {
  from?: string;
  fromName?: string;
}

interface FieldErrors {
  description?: string;
  contactEmail?: string;
}

const SUPPORT_EMAIL = "support@labzero.eco";
const SUBMIT_ERROR = `We couldn't submit your request right now. Please try again, or email ${SUPPORT_EMAIL}.`;
const TURNSTILE_ERROR = `We couldn't load the verification check. Please refresh the page, or email ${SUPPORT_EMAIL}.`;
const TURNSTILE_SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const splitBrowser = (): { name: string; version: string } => {
  const [name = "Unknown", ...rest] = detectBrowser().split(" ");
  return { name, version: rest.join(" ") || "Unknown" };
};

// Only trust an origin we produced ourselves — this string ends up in a support email.
const sameOriginOrNull = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

export const ContactPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const statusRef = useRef<HTMLDivElement>(null);

  const typeParam = searchParams.get("type");
  const defaultType: ContactRequestType = useMemo(() => {
    const match = CONTACT_REQUEST_TYPES.find(type => type.value === typeParam);
    return match ? match.value : "support";
  }, [typeParam]);

  const originState = location.state as ContactLocationState | null;
  const originUrl = sameOriginOrNull(originState?.from);
  const originName = originUrl ? originState?.fromName : undefined;

  const [requestType, setRequestType] = useState<ContactRequestType>(defaultType);
  const [pageUrl, setPageUrl] = useState(originUrl ?? "");
  const [description, setDescription] = useState("");
  const [expectedBehaviour, setExpectedBehaviour] = useState("");
  const [actualBehaviour, setActualBehaviour] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [name, setName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const isBugReport = requestType === "bug";
  // A session already vouches for a signed-in user, so only anonymous posts are challenged.
  const turnstileSiteKey = user ? undefined : TURNSTILE_SITE_KEY;
  // No key configured means no bot protection, so an anonymous form must not open at all.
  const blockedUnprotected = !user && !TURNSTILE_SITE_KEY;

  useEffect(() => {
    setRequestType(defaultType);
  }, [defaultType]);

  // Auth resolves after mount, so the initial value alone misses it. Only fill a
  // field the visitor hasn't started typing in.
  useEffect(() => {
    if (user?.email) setContactEmail(current => current || user.email);
  }, [user?.email]);

  useEffect(() => {
    if (status === "success" || status === "error") {
      statusRef.current?.focus();
    }
  }, [status]);

  // A stale banner over a half-typed message reads as though that one sent too.
  const clearStatus = (): void => {
    if (status === "success" || status === "error") setStatus("idle");
  };

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};

    if (!description.trim()) {
      errors.description = "Please tell us what happened or how we can help";
    }

    const emailError = validateEmail(contactEmail);
    if (emailError) errors.contactEmail = emailError;

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setStatus("submitting");

    const browser = splitBrowser();
    const payload: ContactPayload = {
      request_type: requestType,
      description: description.trim(),
      ...(isBugReport && expectedBehaviour.trim()
        ? { expected_behaviour: expectedBehaviour.trim() }
        : {}),
      ...(isBugReport && actualBehaviour.trim()
        ? { actual_behaviour: actualBehaviour.trim() }
        : {}),
      contact_email: contactEmail.trim(),
      ...(!user && name.trim() ? { name: name.trim() } : {}),
      ...(!user && organisation.trim() ? { organisation: organisation.trim() } : {}),
      browser_name: browser.name,
      browser_version: browser.version,
      os_name: detectOS(),
      timestamp: new Date().toISOString(),
      timezone: getTimezone(),
      ...(isBugReport && pageUrl.trim() ? { page_url: pageUrl.trim() } : {}),
      // The name only describes the page we prefilled, so drop it if that was edited.
      ...(isBugReport && originName && pageUrl.trim() === originUrl
        ? { page_name: originName }
        : {}),
      ...(!user && turnstileToken ? { turnstile_token: turnstileToken } : {}),
    };

    const response = await submitContactForm(payload);

    // Tokens are single use, so a retry needs a fresh one either way.
    turnstileRef.current?.reset();

    if (response.success) {
      setStatus("success");
      setRequestType(defaultType);
      setPageUrl(originUrl ?? "");
      setDescription("");
      setExpectedBehaviour("");
      setActualBehaviour("");
      setContactEmail(user?.email ?? "");
      setName("");
      setOrganisation("");
      return;
    }

    setStatus("error");
  };

  const fieldClass = (base: string, hasError: boolean): string =>
    `w-full ${base} ${hasError ? `${base}-error` : ""}`;

  const descriptionRemaining = CONTACT_LIMITS.description - description.length;

  if (blockedUnprotected) {
    return (
      <section className="secondary-page">
        <div className="max-w-2xl mx-auto lg:pb-16">
          <h1>Contact &amp; Feedback</h1>
          <Alert variant="warning" soft>
            <span>
              This form isn't available right now. Please email {SUPPORT_EMAIL}, or log in
              and send it from inside the app.
            </span>
          </Alert>
        </div>
      </section>
    );
  }

  return (
    <section className="secondary-page">
      {/* secondary-page drops to pb-1 at lg, which leaves the card against the footer */}
      <div className="max-w-2xl mx-auto lg:pb-16">
        <h1>Contact &amp; Feedback</h1>

        <div ref={statusRef} tabIndex={-1} className="mb-4">
          {status === "success" && (
            <Alert variant="success" soft>
              <span>Thanks for your feedback. Your request has been submitted successfully.</span>
            </Alert>
          )}
          {status === "error" && (
            <Alert variant="error" soft>
              <span>{SUBMIT_ERROR}</span>
            </Alert>
          )}
        </div>

        <Card variant="secondary" className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Request type</legend>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
                {CONTACT_REQUEST_TYPES.map(type => (
                  <label
                    key={type.value}
                    htmlFor={`request-type-${type.value}`}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    <input
                      id={`request-type-${type.value}`}
                      type="radio"
                      name="requestType"
                      className="radio radio-primary"
                      value={type.value}
                      checked={requestType === type.value}
                      onChange={() => {
                        setRequestType(type.value);
                        clearStatus();
                      }}
                    />
                    <span className="label-text">{type.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="fieldset">
              <legend id="description-legend" className="fieldset-legend">
                Description (required)
              </legend>
              <p className="label">Tell us what happened or how we can help.</p>
              <textarea
                className={`${fieldClass("textarea", Boolean(fieldErrors.description))} resize-y`}
                rows={5}
                maxLength={CONTACT_LIMITS.description}
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                  clearStatus();
                }}
                aria-labelledby="description-legend"
                aria-describedby={fieldErrors.description ? "description-error" : undefined}
                aria-invalid={Boolean(fieldErrors.description)}
                aria-required
              />
              {fieldErrors.description && (
                <p id="description-error" className="label text-error">
                  {fieldErrors.description}
                </p>
              )}
              {descriptionRemaining <= 500 && (
                <p className="label">{descriptionRemaining} characters left</p>
              )}
            </fieldset>

            {isBugReport && (
              <fieldset className="fieldset">
                <legend id="page-url-legend" className="fieldset-legend">
                  Page this happened on
                </legend>
                <input
                  type="text"
                  className="w-full input"
                  placeholder="https://labzero.eco/dashboard"
                  maxLength={CONTACT_LIMITS.pageUrl}
                  value={pageUrl}
                  onChange={e => {
                    setPageUrl(e.target.value);
                    clearStatus();
                  }}
                  aria-labelledby="page-url-legend"
                  aria-describedby="page-url-help"
                />
                <p id="page-url-help" className="label">
                  {originUrl
                    ? "Filled in from where you clicked Report bug. Edit it if that's not the page."
                    : "Where were you when it happened?"}
                </p>
              </fieldset>
            )}

            {isBugReport && (
              <div className="grid gap-4 md:grid-cols-2">
                <fieldset className="fieldset">
                  <legend id="expected-legend" className="fieldset-legend">
                    Expected behaviour
                  </legend>
                  <textarea
                    className="w-full textarea resize-y"
                    rows={2}
                    maxLength={CONTACT_LIMITS.behaviour}
                    placeholder="What did you expect to happen?"
                    value={expectedBehaviour}
                    onChange={e => {
                      setExpectedBehaviour(e.target.value);
                      clearStatus();
                    }}
                    aria-labelledby="expected-legend"
                  />
                </fieldset>

                <fieldset className="fieldset">
                  <legend id="actual-legend" className="fieldset-legend">
                    Actual behaviour
                  </legend>
                  <textarea
                    className="w-full textarea resize-y"
                    rows={2}
                    maxLength={CONTACT_LIMITS.behaviour}
                    placeholder="What happened instead?"
                    value={actualBehaviour}
                    onChange={e => {
                      setActualBehaviour(e.target.value);
                      clearStatus();
                    }}
                    aria-labelledby="actual-legend"
                  />
                </fieldset>
              </div>
            )}

            {!user && (
              <div className="grid gap-4 md:grid-cols-2">
                <fieldset className="fieldset">
                  <legend id="name-legend" className="fieldset-legend">
                    Your name (optional)
                  </legend>
                  <input
                    type="text"
                    className="w-full input"
                    maxLength={CONTACT_LIMITS.name}
                    value={name}
                    onChange={e => {
                      setName(e.target.value);
                      clearStatus();
                    }}
                    aria-labelledby="name-legend"
                    autoComplete="name"
                  />
                </fieldset>

                <fieldset className="fieldset">
                  <legend id="organisation-legend" className="fieldset-legend">
                    Organisation (optional)
                  </legend>
                  <input
                    type="text"
                    className="w-full input"
                    maxLength={CONTACT_LIMITS.organisation}
                    value={organisation}
                    onChange={e => {
                      setOrganisation(e.target.value);
                      clearStatus();
                    }}
                    aria-labelledby="organisation-legend"
                    aria-describedby="organisation-help"
                    autoComplete="organization"
                  />
                  <p id="organisation-help" className="label">
                    Helps us find your account faster
                  </p>
                </fieldset>
              </div>
            )}

            <fieldset className="fieldset">
              <legend id="contact-email-legend" className="fieldset-legend">
                Contact email (required)
              </legend>
              <input
                type="email"
                className={fieldClass("input", Boolean(fieldErrors.contactEmail))}
                placeholder="you@researchcentre.org"
                maxLength={CONTACT_LIMITS.email}
                value={contactEmail}
                onChange={e => {
                  setContactEmail(e.target.value);
                  clearStatus();
                }}
                aria-labelledby="contact-email-legend"
                aria-describedby={
                  fieldErrors.contactEmail ? "contact-email-error" : "contact-email-help"
                }
                aria-invalid={Boolean(fieldErrors.contactEmail)}
                aria-required
                autoComplete="email"
              />
              {fieldErrors.contactEmail ? (
                <p id="contact-email-error" className="label text-error">
                  {fieldErrors.contactEmail}
                </p>
              ) : (
                <p id="contact-email-help" className="label">So we can reply to you</p>
              )}
            </fieldset>


            {turnstileSiteKey && (
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Verification</legend>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onToken={token => {
                    setTurnstileToken(token);
                    if (token) setTurnstileFailed(false);
                  }}
                  onError={() => setTurnstileFailed(true)}
                />
                {turnstileFailed && (
                  <p className="label text-error">{TURNSTILE_ERROR}</p>
                )}
              </fieldset>
            )}

            <button
              type="submit"
              className="w-full btn btn-primary"
              disabled={status === "submitting" || Boolean(turnstileSiteKey && !turnstileToken)}
            >
              {status === "submitting" ? (
                <span className="loading loading-spinner"></span>
              ) : (
                "Submit"
              )}
            </button>

            {turnstileSiteKey && !turnstileToken && !turnstileFailed && (
              <p className="text-sm text-base-content/70">
                Finish the verification above to send your request.
              </p>
            )}
          </form>
        </Card>

        {originUrl && (
          <p className="mt-4 text-sm">
            <Link to={new URL(originUrl).pathname} className="link">
              Back to {originName || "where you were"}
            </Link>
          </p>
        )}
      </div>
    </section>
  );
};
